import * as React from 'react';

import {
  Button,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
  TextField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import * as state from '../../_state/userDialog.state';
import { useTranslations } from '../../i18n/useTranslations';

import { SelectOrganization } from '~/shared/components/select-fields/SelectOrganization';
import { SelectTimeZone } from '~/shared/components/select-fields/SelectTimeZone';

export const UserDetailsDialog = () => {
  const t = useTranslations();
  const toast = useToast();

  const [isOpen, setIsOpen] = useAtom(state.$userDialogOpen);
  const mode = useAtomValue(state.$userDialogMode);
  const formData = useAtomValue(state.$userFormData);
  const isFormValid = useAtomValue(state.$userFormValid);
  const isSaving = useAtomValue(state.$userDialogSaving);

  const updateFormData = useSetAtom(state.$updateUserFormData);
  const saveUser = useSetAtom(state.$saveUser);
  const closeDialog = useSetAtom(state.$closeUserDialog);

  const handleSave = React.useCallback(async () => {
    try {
      await saveUser();
      toast.success(
        mode === 'create'
          ? t.userDetailsDialog.saveSuccess.create
          : t.userDetailsDialog.saveSuccess.update,
      );
    } catch (_error) {
      toast.error(t.userDetailsDialog.saveError);
    }
  }, [saveUser, mode, toast, t]);

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={() => setIsOpen(false)}
      className="@UserDetailsDialog"
    >
      <DialogContent className="min-w-[500px]">
        <DialogHeader slot="title">
          <DialogTitle>
            {mode === 'create'
              ? t.userDetailsDialog.title.create
              : t.userDetailsDialog.title.edit}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 p-4">
          <div className="flex gap-4">
            <TextField
              aria-label={t.userDetailsDialog.fields.firstName.label}
              label={t.userDetailsDialog.fields.firstName.label}
              placeholder={t.userDetailsDialog.fields.firstName.placeholder}
              value={formData.firstName}
              onChange={(value) => updateFormData({ firstName: value })}
              isRequired
              autoFocus
              className="flex-1"
            />

            <TextField
              aria-label={t.userDetailsDialog.fields.lastName.label}
              label={t.userDetailsDialog.fields.lastName.label}
              placeholder={t.userDetailsDialog.fields.lastName.placeholder}
              value={formData.lastName}
              onChange={(value) => updateFormData({ lastName: value })}
              isRequired
              className="flex-1"
            />
          </div>

          <TextField
            aria-label={t.userDetailsDialog.fields.email.label}
            label={t.userDetailsDialog.fields.email.label}
            placeholder={t.userDetailsDialog.fields.email.placeholder}
            value={formData.email}
            onChange={(value) => updateFormData({ email: value })}
            isRequired
            type="email"
          />

          <TextField
            aria-label={t.userDetailsDialog.fields.phoneNumber.label}
            label={t.userDetailsDialog.fields.phoneNumber.label}
            placeholder={t.userDetailsDialog.fields.phoneNumber.placeholder}
            value={formData.phoneNumber ?? ''}
            onChange={(value) =>
              updateFormData({ phoneNumber: value || undefined })
            }
          />

          {mode === 'create' && (
            <SelectOrganization
              label={t.userDetailsDialog.fields.organization.label}
              placeholder={t.userDetailsDialog.fields.organization.placeholder}
              value={formData.organizationId}
              onChange={(value) =>
                updateFormData({
                  organizationId: value,
                })
              }
            />
          )}

          <SelectTimeZone
            label={t.userDetailsDialog.fields.timezone.label}
            placeholder={t.userDetailsDialog.fields.timezone.placeholder}
            value={formData.timezone}
            onChange={(value) => updateFormData({ timezone: value })}
            isRequired
          />

          <TextField
            aria-label={t.userDetailsDialog.fields.idpId.label}
            label={t.userDetailsDialog.fields.idpId.label}
            placeholder={t.userDetailsDialog.fields.idpId.placeholder}
            value={formData.idpId ?? ''}
            onChange={(value) => updateFormData({ idpId: value || undefined })}
            description={t.userDetailsDialog.fields.idpId.description}
          />
        </div>

        <DialogFooter>
          <Button
            aria-label={t.userDetailsDialog.actions.cancel}
            variant="outline"
            color="neutral"
            onPress={closeDialog}
            isDisabled={isSaving}
          >
            {t.userDetailsDialog.actions.cancel}
          </Button>
          <Button
            aria-label={
              mode === 'create'
                ? t.userDetailsDialog.actions.create
                : t.userDetailsDialog.actions.save
            }
            variant="solid"
            color="primary"
            onPress={handleSave}
            isDisabled={!isFormValid || isSaving}
            isLoading={isSaving}
          >
            {mode === 'create'
              ? t.userDetailsDialog.actions.create
              : t.userDetailsDialog.actions.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
